// src/layouts/ReportLayout/index.js
import React from 'react';
import { Box } from '@mui/material';
import DashboardLayout from 'layouts/DashboardLayout';
import DashboardNavbar from 'components/RDNavbars/DashboardNavbar';
import ReportHeader from 'components/ReportHeader'; // Our new ReportHeader component

/**
 * Standard layout wrapper for all report pages
 * Uses the new flexible ReportHeader component
 * 
 * Built following project architecture principles:
 * - Maintainability first
 * - Multi-tenant support via client theming
 * - Minimal dependencies
 * - Clean, reusable patterns
 * 
 * @param {Object} props
 * @param {string|React.ReactNode} props.title - Report title
 * @param {string} props.subtitle - Optional subtitle
 * @param {React.ReactNode} props.children - Report content
 * @param {Object} props.headerProps - Props passed to ReportHeader
 * @param {React.ReactNode[]} props.headerProps.leftControls - Controls after title
 * @param {React.ReactNode[]} props.headerProps.centerControls - Centered controls
 * @param {React.ReactNode[]} props.headerProps.rightControls - Right-aligned controls
 * @param {Object} props.headerProps.theme - Client-specific theme
 * @param {Object} props.containerProps - Props passed to main content container
 * @param {boolean} props.showHeader - Whether to show header (default: true)
 */
export default function ReportLayout({ 
  title,
  subtitle,
  children,
  headerProps = {},
  containerProps = {},
  showHeader = true
}) {
  // Default client theme - can be overridden via headerProps.theme
  const defaultTheme = {
    backgroundColor: '#2c3e50',
    titleColor: '#f1c40f',
    textColor: '#ffffff'
  };

  // Merge default theme with any custom theme
  const finalTheme = {
    ...defaultTheme,
    ...headerProps.theme
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      
      {/* New Flexible Report Header */}
      {showHeader && (
        <ReportHeader 
          title={title}
          subtitle={subtitle}
          theme={finalTheme}
          leftControls={[]}
          centerControls={[]}
          rightControls={[]}
          minWidth={headerProps.minWidth}
          maxWidth={headerProps.maxWidth}
          {...headerProps}
        />
      )}
      
      {/* Main Report Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 2,
          px: 3,
          minWidth: headerProps.minWidth || '700px',
          maxWidth: headerProps.maxWidth || 'none',
          margin: headerProps.maxWidth ? '0 auto' : undefined,
          ...containerProps.sx
        }}
        {...containerProps}
      >
        {children}
      </Box>
    </DashboardLayout>
  );
}